import { parseExpression, ParserOptions } from 'cron-parser';
import { Cron, CronOptions } from 'croner';
import dayjs from 'dayjs';

type Recurrence = 'YEARLY' | 'MONTHLY';

/**
 * Creates a cron job string from a date and a recurrence
 *
 * @export
 * @param date The date that will be used to create the cron string
 * @param recurrence The recurrence of the cron job
 * @param timeZone The timezone of the result cron string
 * @return The cron string
 */
export function createCronStringFromDate(
  date: Date,
  recurrence: Recurrence,
  timeZone = 'Europe/Brussels'
): string {
  if (Number.isNaN(date.valueOf())) {
    throw new Error('Invalid date provided');
  }

  const baseCronString = recurrence === 'YEARLY' ? '0 0 D M *' : '0 0 D * *';

  //we lock the locale to parse the date string in a known format
  //we specify the timeZone to get the dayOfMonth and month
  //in the expected caller time zone
  const dateString = date.toLocaleDateString('fr-BE', { timeZone: timeZone });

  //we extract the dayOfMonth and month from the date string
  //by parsing the string to make sure we have the dayOfMonth and month
  //in the expected called time zone
  const dayOfMonth = dateString.split('/')[0].replace(/^0+/, '');
  const month = dateString.split('/')[1].replace(/^0+/, '');

  const cronString = baseCronString
    .replace('D', dayOfMonth)
    .replace('M', month);

  return cronString;
}

/**
 * Return next occurence of a cron job string.
 *
 * @export
 * @param {string} cron
 * @param {Date} startDate
 * @param {boolean} [isStartDateEligible=false] Should the startDate be eligible for the next occurrence.
 * @return {Date} Date in Europe/Brussels timezone
 */
export function getNextOccurrence(cron: string, startDate: Date): Date | null {
  if (!startDate || cron === '') {
    return null;
  }

  const options: CronOptions = {
    timezone: 'Europe/Brussels'
  };

  const cronJob = Cron(cron, options);

  return cronJob.nextRun(startDate);
}

export function getNextOccurrences(
  cron: string,
  startDate: Date,
  nbOccurrences: number
): Date[] {
  const occurrences: Date[] = [];
  let previousOccurrence: Date | null = null;

  for (let i = 0; i <= nbOccurrences; i++) {
    previousOccurrence = getNextOccurrence(
      cron,
      previousOccurrence ?? startDate
    );

    if (previousOccurrence) {
      occurrences.push(previousOccurrence);
    }
  }

  return occurrences;
}

/**
 * Return previous occurence of a cron job string
 * The startdate is also egible for the previous occurence
 *
 * @export
 * @param {string} cron
 * @param {Date} startDate
 * @return {Date}  Date in Europe/Brussels timezone
 */
export function getPreviousOccurrence(cron: string, startDate: Date): Date {
  const options: ParserOptions<true> = {
    currentDate: dayjs(startDate).add(1, 'millisecond').toDate(),
    iterator: true
  };

  const interval = parseExpression(cron, options);
  return interval.prev().value.toDate();
}
